import React, { useState } from "react";
import {
  TextField,
  IconButton,
  Button,
  Typography,
  InputAdornment,
  Grid,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import loginBgimg from "../../Assest/Images/loginBgImg.svg";
import loginSideimg from "../../Assest/Images/loginSideImg.svg";
import EmailIcon from "@mui/icons-material/Email";
import { loginPostData, fetchData } from "../../BaseUrl";
import useTranslations from "../Translations/useTranslations";
import { useNavigate } from "react-router-dom";
import Routings from "../../Routings";

const Login = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTab = useMediaQuery("(max-width:1024px)");
  const { translations, languageSelect } = useTranslations();
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [formValues, setFormValues] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });
  const [focusedField, setFocusedField] = useState(null); // Track which field is focused

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const validateField = (fieldName, value) => {
    let error = "";

    if (fieldName === "email") {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!value) {
        error = translations.emailRequired;
      } else if (!emailRegex.test(value)) {
        error = translations.emailInvalid;
      }
    }

    if (fieldName === "password") {
      if (!value) {
        error = translations.passwordRequired;
      } else if (value.length < 6) {
        error = translations.passwordInvalid;
      }
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: error,
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
    setFocusedField(null); // Reset focused field on blur
  };

  const handleFocus = (e) => {
    setFocusedField(e.target.name); // Track which field is focused
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = Object.values(errors).every((error) => error === "");
    if (isValid) {
      try {
        const result = await loginPostData("auth/login", formValues);
        sessionStorage.setItem("role", result.data);
        if (result.data) {
          // Fetch and store user info for student components
          try {
            const meResult = await fetchData("auth/me");
            if (meResult?.data) {
              sessionStorage.setItem("userEmail", meResult.data.email || "");
              sessionStorage.setItem("userFullName", meResult.data.fullName || "");
              sessionStorage.setItem("applicationId", meResult.data.applicationId || "");
              sessionStorage.setItem("profilePic", meResult.data.profilePic || "");
              sessionStorage.setItem("userId", meResult.data.id ? String(meResult.data.id) : "");
            }
          } catch (_) { /* non-blocking */ }
          navigate("/dashboard/registrations");
          // navigate(Routings.DASHBOARD + "/" + Routings.ADMIN_REGISTRATIONS);
        }

      } catch (err) {
        console.error("Error posting data:", err);
      }
    }
  };

  const styles = {
    container: {
      backgroundColor: "#F9F6FF",
      background: isTab ? "#F9F6FF" : `url(${loginBgimg})`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      minHeight: "100vh",
      marginTop: "-2rem"
    },
    heading: {
      fontSize: languageSelect === "Tamil" ? "18px" : "28px",
      fontWeight: "bold",
      color: "#333",
      marginBottom: "10px",
      textAlign: "left",
    },
    subheading: {
      fontSize: languageSelect === "Tamil" ? "12px" : "14px",
      color: "rgba(58, 57, 57, 0.5)",
      marginBottom: "20px",
      textAlign: "left",
    },
    button: {
      background: "linear-gradient(90deg, #4A7BA8, #244C77)",
      color: "#fff",
      fontSize: languageSelect === "Tamil" ? "14px" : "16px",
      textTransform: "none",
      padding: "5px 30px",
      borderRadius: "20px",
      marginTop: "1rem",
    },
    link: {
      color: "#2390FF",
      fontSize: languageSelect === "Tamil" ? "10px" : "14px",
      textDecoration: "none",
      cursor: "pointer",
      marginTop: "10px",
    },
  };

  const isFormValid =
    Object.values(formValues).every((value) => value.trim() !== "") &&
    Object.values(errors).every((error) => error === "");
  return (
    <div style={styles.container}>
      <Grid container pt={"8rem"}>
        <Grid
          xs={12}
          lg={5}
          mt={2}
          sx={{ padding: isMobile ? "2rem" : "2rem 4rem 2rem 6rem", mt: 4 }}
        >
          <Typography style={styles.heading}>
            {languageSelect === "Tamil"
              ? translations.Login
              : translations.welcometo}
            <span style={{ color: "#244C77" }}>
              {languageSelect === "Tamil"
                ? translations.welcometo
                : translations.Login}
            </span>
          </Typography>
          <Typography style={styles.subheading}>
            {translations.keepConnected}
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              size="small"
              fullWidth
              placeholder={translations.emailPlaceholder}
              variant="outlined"
              margin="normal"
              name="email"
              value={formValues.email}
              onChange={handleInputChange}
              onBlur={handleBlur} // Trigger validation on blur
              onFocus={handleFocus} // Track focus
              error={!!errors.email && focusedField !== "email"} // Show error only when field is blurred
              helperText={focusedField !== "email" ? errors.email : ""}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder={translations.passwordPlaceholder}
              variant="outlined"
              margin="normal"
              name="password"
              value={formValues.password}
              onChange={handleInputChange}
              onBlur={handleBlur} // Trigger validation on blur
              onFocus={handleFocus} // Track focus
              type={passwordVisible ? "text" : "password"}
              error={!!errors.password && focusedField !== "password"} // Show error only when field is blurred
              helperText={focusedField !== "password" ? errors.password : ""}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      style={{ color: "gray" }}
                    >
                      {passwordVisible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />
            <div
              style={{
                display: "flex",
                justifyContent:
                  languageSelect === "Tamil" ? "" : "space-between",
              }}
            >
              <FormControlLabel
                control={<Checkbox size="small" />}
                label={translations.rememberMe}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: languageSelect === "Tamil" ? "9px" : "14px",
                  },
                }}
              />
              <Typography style={{ ...styles.link }}>
                {translations.forgotPassword}
              </Typography>
            </div>
            <Button
              disabled={!isFormValid}
              variant="contained"
              type="submit"
              style={styles.button}
            >
              {translations.loginButton}
            </Button>
          </form>
          <Typography
            style={{
              marginTop: "20px",
              marginBottom: "10px",
              fontSize: languageSelect === "Tamil" ? "10px" : "14px",
              color: "#aaa",
              textAlign: "center",
              position: "relative",
            }}
          >
            <span
              style={{
                position: "absolute",
                left: "0",
                right: "0",
                top: "50%",
                borderBottom: "1px solid #aaa",
                transform: "translateY(-80%)",
                width: "30%",
                margin: "0 5%",
              }}
            />
            {translations.orLoginWith}{" "}
            <span
              style={{
                position: "absolute",
                right: "0",
                top: "50%",
                borderBottom: "1px solid #aaa",
                transform: "translateX(0%)",
                width: "30%",
                margin: "0 5%",
              }}
            />
          </Typography>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 20,
            }}
          >
            <img
              width="25"
              height="25"
              src="https://img.icons8.com/color/48/google-logo.png"
              alt="google-logo"
            />
            <img
              width="25"
              height="25"
              src="https://img.icons8.com/color/48/ms-outlook.png"
              alt="ms-outlook"
            />
            <img
              width="25"
              height="25"
              src="https://img.icons8.com/ios/50/2390FF/twitterx--v2.png"
              alt="twitterx--v2"
            />
          </div>
        </Grid>
        <Grid xs={12} lg={7}>
          {!isTab && (
            <img
              src={loginSideimg}
              alt="loginSideimg"
              style={{
                width: "100%",
                minHeight: "88vh",
                objectFit: "cover",
                display: "block",
                marginTop: "-2rem",
              }}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default Login;
